import { useCallback, useEffect } from 'react';
import omitBy from 'lodash.omitby';
import { useAtomValue, useSetAtom } from 'jotai';
import { FieldValues } from 'react-hook-form';

import { JobCoordinateParamsType, JobPositionListParamsType } from '@restworld/data-services';
import { useMapBoundingRadius, formatExplorerOverallFilterData } from '@restworld/utils-common';
import type { MultiSelectOptionType } from '@restworld/utils-common';

import { defaultJobListPayload, URLDataType } from '../job-explorer.types';
import { getMapRefAtom, jobCoordinateParamsAtom, setUserCoordinatesAtom } from '../job-explorer-map.atoms';
import {
  moreFiltersAtom,
  searchBarUrlParamsAtom,
  filterContextValuesAtom,
  jobExplorerPageContentAtom,
} from '../job-explorer-filters.atoms';
import { jobListParamsAtom } from '../job-explorer-list.atoms';

type SearchBarFilterType = {
  domain_interest_ids?: string[];
  occupation_ids?: number[];
};

const getFormattedOccupationFilters = (occupation: MultiSelectOptionType[]): SearchBarFilterType =>
  occupation.reduce((prev, curr) => {
    if (curr.type === 'occupations') {
      return {
        ...prev,
        occupation_ids: prev.occupation_ids ? [...prev.occupation_ids, +curr.value] : [+curr.value],
      };
    }
    if (curr.type === 'domain_interest') {
      return {
        ...prev,
        domain_interest_ids: prev.domain_interest_ids ? [...prev.domain_interest_ids, curr.value] : [curr.value],
      };
    }
    return prev;
  }, {} as SearchBarFilterType);

const parseUrlParam = (param: string | null) => {
  if (!param) return undefined;
  try {
    return JSON.parse(decodeURIComponent(param));
  } catch {
    return undefined;
  }
};

export const useExplorerFilterUrlParams = () => {
  const setMoreFilters = useSetAtom(moreFiltersAtom);
  const setSearchBarUrlParams = useSetAtom(searchBarUrlParamsAtom);
  const setFilterContextValues = useSetAtom(filterContextValuesAtom);
  const mapRef = useAtomValue(getMapRefAtom);
  const setUserCoordinates = useSetAtom(setUserCoordinatesAtom);
  const { getRadius } = useMapBoundingRadius({ map: mapRef?.current });
  const setJobListParams = useSetAtom(jobListParamsAtom);
  const setJobCoordinateParams = useSetAtom(jobCoordinateParamsAtom);
  const setJobExplorerPageContent = useSetAtom(jobExplorerPageContentAtom);

  const getUrlData = useCallback(() => {
    if (typeof window === 'undefined') return { isSearchBarUrlParamsAvailable: false } as URLDataType;

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const searchBarFiltersRaw = urlParams.get('search_bar_filters');
      const overallFiltersRaw = urlParams.get('overall_filters');
      const mapFiltersRaw = urlParams.get('map_filters');

      const searchBarFilterParams = (parseUrlParam(searchBarFiltersRaw) as MultiSelectOptionType[] | undefined) ?? [];
      const overallFilterParams = (parseUrlParam(overallFiltersRaw) as FieldValues | undefined) ?? {};
      const mapFiltersParams = (parseUrlParam(mapFiltersRaw) as FieldValues | undefined) ?? {};

      const locationUrlParams = searchBarFilterParams.find((v) => v.key === 'selected_location') as any;
      const occupationUrlParams = searchBarFilterParams.filter((v) => v.key !== 'selected_location');

      const searchBarFilterRadius = locationUrlParams
        ? getRadius({ lon: locationUrlParams?.value?.['lng'], lat: locationUrlParams?.value['lat'] })
        : null;

      const mapFilterRadius = mapFiltersParams['longitude']
        ? getRadius({
            lon: mapFiltersParams['longitude'],
            lat: mapFiltersParams['latitude'],
          })
        : null;

      const searchBarFilters = occupationUrlParams.length ? getFormattedOccupationFilters(occupationUrlParams) : {};
      const doesFilterExist = Object.keys(searchBarFilters).length > 0;
      const formatOverallFilter = Object.keys(overallFilterParams).length
        ? formatExplorerOverallFilterData({ values: overallFilterParams })
        : {};

      const urlParamsPayload = {
        ...(locationUrlParams ? { lon: locationUrlParams?.value['lng'], lat: locationUrlParams?.value['lat'] } : {}),
        ...(searchBarFilterRadius ? { radius: searchBarFilterRadius } : {}),
        ...(mapFiltersParams['longitude'] && mapFiltersParams['latitude']
          ? { lon: mapFiltersParams['longitude'], lat: mapFiltersParams['latitude'] }
          : {}),
        ...(mapFilterRadius ? { radius: mapFilterRadius } : {}),
        ...(doesFilterExist ? { search_bar_filters: searchBarFilters } : {}),
        ...formatOverallFilter,
      };

      const jobListUrlParams: JobPositionListParamsType = {
        ...(Object.keys(urlParamsPayload).length ? { ...defaultJobListPayload } : {}),
        ...urlParamsPayload,
      };

      const jobLocationUrlParams: JobCoordinateParamsType = {
        ...(doesFilterExist ? { search_bar_filters: JSON.stringify(searchBarFilters) } : {}),
        ...formatOverallFilter,
      };

      const urlData: URLDataType = {
        locationUrlParams,
        occupationUrlParams,
        isSearchBarUrlParamsAvailable: true,
        jobListUrlParams,
        jobLocationUrlParams,
        parsedOverallFilter: overallFilterParams,
        mapFiltersURLParams: mapFiltersParams,
      };

      if (Object.keys(jobListUrlParams).length) setJobListParams((p) => ({ ...p, ...jobListUrlParams }));
      if (Object.keys(jobLocationUrlParams).length) setJobCoordinateParams((p) => ({ ...p, ...jobLocationUrlParams }));
      setSearchBarUrlParams(urlData);
      if (overallFilterParams) setFilterContextValues(overallFilterParams);

      setMoreFilters((p) => {
        const filterParams = omitBy(
          { ...formatOverallFilter },
          (value, key) =>
            typeof value === 'undefined' ||
            ['min_salary', 'max_salary', 'user_location_lat', 'user_location_lon', 'benefits'].includes(key)
        );
        return {
          ...p,
          ...(Object.keys(filterParams).length ? filterParams : {}),
        };
      });

      setUserCoordinates({
        lat: locationUrlParams?.value?.lat,
        lon: locationUrlParams?.value?.lng,
      });

      if (formatOverallFilter['page_content']) setJobExplorerPageContent(formatOverallFilter['page_content']);
    } catch (error) {
      console.error('error getting data from url params', error);
    }
  }, [
    getRadius,
    setFilterContextValues,
    setJobCoordinateParams,
    setJobExplorerPageContent,
    setJobListParams,
    setMoreFilters,
    setSearchBarUrlParams,
    setUserCoordinates,
  ]);

  useEffect(() => {
    getUrlData();
  }, [getUrlData]);
};
